/**
 * The navigation, as DATA.
 *
 * Adding a destination is a config line here, not a renderer. Before this
 * module the pro rail rendered its nav SIX ways in one file (only the first
 * of them data-driven) and the admin rail was a second 393-line file with its
 * own copy of the badge machinery. One `<NavRail>` reads this.
 *
 * Shape notes, all of them load-bearing:
 *
 * - **No `exact` flag.** Which row is current is resolved by LONGEST matching
 *   href (`resolveActiveHref`), segment-aware. That kills the whole class of
 *   prefix collisions the old flag existed to patch — `/settings/account` no
 *   longer lights on `/settings/security`, the admin `/admin` row no longer
 *   lights on `/admin/pros`, and `/` is naturally exact because nothing can be
 *   a segment-prefix of the root.
 * - **No icons.** The Job Book rail is text and figures: a row is a label and
 *   a right-aligned tabular count. The mockup (`.context/mockups/shell.html`)
 *   draws no icons in the rail, so the config carries none. The mobile bottom
 *   nav does have them, and owns its own icon map.
 * - **Counts are a key, not a number.** An entry declares *that* it can carry
 *   a count (`badge: "leads"`); the layout passes whatever counts it actually
 *   has. A key with no data renders no count — so `today` and `leads` are
 *   declared (they are in the mockup) without this module inventing a query
 *   for them. Wire them up where the data lands, not here.
 */

/** Which counted things a row can show. The layout supplies the numbers. */
export type NavBadgeKey = "today" | "leads" | "studio" | "notifications";

/** Counts by key. Absent or <= 0 renders nothing. */
export type NavBadges = Partial<Record<NavBadgeKey, number>>;

/** An href that is only known at runtime (it depends on fetched data). */
export type NavDynamicHref = "marketplaceProfile";

/** A non-navigating row. The rail fires it; it never changes the URL. */
export type NavActionId = "open-feedback";

/** Gates beyond the pro role, for the two cross-rail links. */
export type NavVisibility = "isAdmin" | "hasProAccess";

type NavGate = {
	/** Pro roles that may see this. Undefined = every role. Admins see all. */
	roles?: string[];
	when?: NavVisibility;
};

export type NavLink = NavGate & {
	kind: "link";
	name: string;
	href: string;
	badge?: NavBadgeKey;
};

export type NavExternalLink = NavGate & {
	kind: "external";
	name: string;
	dynamic: NavDynamicHref;
};

export type NavActionItem = NavGate & {
	kind: "action";
	name: string;
	action: NavActionId;
};

export type NavGroup = NavGate & {
	kind: "group";
	name: string;
	/**
	 * `section` is a `.label` head (caps, tabular) over indented children —
	 * SELL, SHOW, OVERVIEW. `submenu` is an item-styled disclosure that sits
	 * in the flow of ordinary rows: `Settings ▾` is one row, not four.
	 */
	style: "section" | "submenu";
	items: NavItem[];
};

/** A hairline. Not a box. Dropped when it would lead, trail or double up. */
export type NavDivider = { kind: "divider" };

export type NavItem = NavLink | NavExternalLink | NavActionItem;
export type NavEntry = NavItem | NavGroup | NavDivider;

/** Same as `NavExternalLink` once its runtime href is known. */
export type ResolvedExternalLink = Omit<NavExternalLink, "dynamic"> & {
	href: string;
};
export type ResolvedItem = NavLink | ResolvedExternalLink | NavActionItem;
export type ResolvedGroup = Omit<NavGroup, "items"> & {
	items: ResolvedItem[];
};
export type ResolvedEntry = ResolvedItem | ResolvedGroup | NavDivider;

export type NavContext = {
	proRole?: string | null;
	isAdmin?: boolean;
	hasProAccess?: boolean;
	hrefs?: Partial<Record<NavDynamicHref, string | null>>;
};

/**
 * The pro rail. Five rows for a solo owner: Today, Leads, the four SHOW rows
 * it cares about, the three utility rows, and `Settings ▾` closed.
 */
export const PRO_NAV: NavEntry[] = [
	{
		kind: "link",
		name: "Today",
		href: "/",
		badge: "today",
		when: "hasProAccess",
	},
	{ kind: "divider" },
	{
		kind: "group",
		name: "Sell",
		style: "section",
		when: "hasProAccess",
		items: [{ kind: "link", name: "Leads", href: "/leads", badge: "leads" }],
	},
	{ kind: "divider" },
	{
		kind: "group",
		name: "Show",
		style: "section",
		when: "hasProAccess",
		items: [
			{ kind: "link", name: "Projects", href: "/projects" },
			{ kind: "link", name: "Website", href: "/website", roles: ["owner"] },
			{
				kind: "link",
				name: "Blog",
				href: "/blog",
				roles: ["owner", "manager"],
			},
			{
				kind: "link",
				name: "Studio",
				href: "/studio",
				badge: "studio",
			},
			{
				kind: "link",
				name: "Insights",
				href: "/insights",
				roles: ["owner", "manager"],
			},
		],
	},
	{ kind: "divider" },
	{
		kind: "link",
		name: "Notifications",
		href: "/notifications",
		badge: "notifications",
	},
	{
		kind: "action",
		name: "Feedback",
		action: "open-feedback",
		when: "hasProAccess",
	},
	{
		kind: "external",
		name: "Marketplace",
		dynamic: "marketplaceProfile",
		when: "hasProAccess",
	},
	{
		kind: "group",
		name: "Settings",
		style: "submenu",
		items: [
			{
				kind: "link",
				name: "Studio profile",
				href: "/settings/studio",
				roles: ["owner", "manager"],
			},
			{ kind: "link", name: "Account", href: "/settings/account" },
			{ kind: "link", name: "Security", href: "/settings/security" },
			{
				kind: "link",
				name: "Team",
				href: "/settings/team",
				roles: ["owner", "manager"],
			},
			{
				kind: "link",
				name: "Pipeline & sources",
				href: "/settings/pipeline",
				roles: ["owner", "manager"],
			},
		],
	},
	{ kind: "divider" },
	{ kind: "link", name: "Admin panel", href: "/admin", when: "isAdmin" },
];

/**
 * The admin rail: 21 destinations across 6 sections, down from 33 — the whole
 * 12-item "Partner recruitment" section went with the partner programme in
 * phase 1.
 *
 * Plan §1.8 states 21 and names 20, and the mockup draws the 20 it names with
 * "Social studio assets" as one placeholder row. There is no
 * `/admin/social-studio` index route for that row to point at, so the two real
 * routes are listed as two rows — which is also what makes the count 21.
 */
export const ADMIN_NAV: NavEntry[] = [
	{
		kind: "group",
		name: "Overview",
		style: "section",
		items: [
			{ kind: "link", name: "Dashboard", href: "/admin" },
			{ kind: "link", name: "Analytics", href: "/admin/analytics" },
			{
				kind: "link",
				name: "Leadership metrics",
				href: "/internal/metrics",
			},
		],
	},
	{ kind: "divider" },
	{
		kind: "group",
		name: "Supply",
		style: "section",
		items: [
			{ kind: "link", name: "Pros", href: "/admin/pros" },
			{ kind: "link", name: "Projects", href: "/admin/projects" },
			{ kind: "link", name: "Pro imports", href: "/admin/imports" },
		],
	},
	{ kind: "divider" },
	{
		kind: "group",
		name: "Demand",
		style: "section",
		items: [
			{ kind: "link", name: "Web leads", href: "/admin/web-leads" },
			{ kind: "link", name: "Consultations", href: "/admin/consultations" },
		],
	},
	{ kind: "divider" },
	{
		kind: "group",
		name: "Marketplace",
		style: "section",
		items: [
			{ kind: "link", name: "Home page", href: "/admin/home-page" },
			{
				kind: "link",
				name: "Room categories",
				href: "/admin/room-categories",
			},
			{ kind: "link", name: "Taxonomy", href: "/admin/taxonomy" },
			{
				kind: "link",
				name: "Cost calculator",
				href: "/admin/calculator-config",
			},
			{ kind: "link", name: "Blogs", href: "/admin/blogs" },
			{ kind: "link", name: "Search", href: "/admin/search" },
		],
	},
	{ kind: "divider" },
	{
		kind: "group",
		name: "Messaging",
		style: "section",
		items: [
			{ kind: "link", name: "WhatsApp", href: "/admin/whatsapp" },
			{
				kind: "link",
				name: "Communications",
				href: "/admin/communications",
			},
			{
				kind: "link",
				name: "Notification test",
				href: "/admin/notifications/test",
			},
		],
	},
	{ kind: "divider" },
	{
		kind: "group",
		name: "Platform",
		style: "section",
		items: [
			{ kind: "link", name: "Users & roles", href: "/admin/users" },
			{ kind: "link", name: "Feedback", href: "/admin/feedback" },
			{
				kind: "link",
				name: "Studio music",
				href: "/admin/social-studio/music",
			},
			{
				kind: "link",
				name: "Studio templates",
				href: "/admin/social-studio/templates",
			},
		],
	},
	{ kind: "divider" },
	{ kind: "link", name: "Pro portal", href: "/", when: "hasProAccess" },
];

/**
 * Which sections are folded is remembered per rail.
 *
 * The admin rail keeps its original key so operators do not lose the folds
 * they already set; the pro rail is new and gets a new one.
 */
export const NAV_STORAGE_KEYS = {
	pro: "nav-rail:sections:pro",
	admin: "admin-sidebar:sections",
} as const;

/**
 * The four fixed mobile slots (plan §1.9). The same four for everyone,
 * because there is one role shape in scope — no supervisor rail, no
 * procurement rail. Everything else is behind More.
 */
export const BOTTOM_NAV_HREFS = ["/", "/leads", "/projects"] as const;

function isVisible(gate: NavGate, ctx: NavContext): boolean {
	if (gate.when === "isAdmin" && !ctx.isAdmin) return false;
	if (gate.when === "hasProAccess" && !ctx.hasProAccess) return false;
	if (!gate.roles) return true;
	// Admins see every pro row, exactly as the old rail did.
	if (ctx.isAdmin) return true;
	return !!ctx.proRole && gate.roles.includes(ctx.proRole);
}

/** An item with its runtime href filled in, or null if it has nowhere to go. */
function resolveItem(item: NavItem, ctx: NavContext): ResolvedItem | null {
	if (!isVisible(item, ctx)) return null;
	if (item.kind !== "external") return item;
	const href = ctx.hrefs?.[item.dynamic];
	if (!href) return null;
	const { dynamic: _dynamic, ...rest } = item;
	return { ...rest, href };
}

/** Leading, trailing and doubled-up hairlines are noise. Drop them. */
function collapseDividers(entries: ResolvedEntry[]): ResolvedEntry[] {
	const out: ResolvedEntry[] = [];
	for (const entry of entries) {
		if (entry.kind !== "divider") {
			out.push(entry);
			continue;
		}
		const previous = out[out.length - 1];
		if (previous && previous.kind !== "divider") out.push(entry);
	}
	while (
		out.length > 0 &&
		(out[out.length - 1] as ResolvedEntry).kind === "divider"
	) {
		out.pop();
	}
	return out;
}

/**
 * Config + who is looking → what the rail renders. Pure, so the gating rules
 * are testable without a DOM.
 */
export function resolveNav(
	entries: NavEntry[],
	ctx: NavContext,
): ResolvedEntry[] {
	const resolved: ResolvedEntry[] = [];
	for (const entry of entries) {
		if (entry.kind === "divider") {
			resolved.push(entry);
			continue;
		}
		if (entry.kind === "group") {
			if (!isVisible(entry, ctx)) continue;
			const items = entry.items
				.map((item) => resolveItem(item, ctx))
				.filter((item): item is ResolvedItem => item !== null);
			// A head with nothing under it is a lie about where you can go.
			if (items.length > 0) resolved.push({ ...entry, items });
			continue;
		}
		const item = resolveItem(entry, ctx);
		if (item) resolved.push(item);
	}
	return collapseDividers(resolved);
}

/** Every navigable row, groups flattened, in rail order. */
export function flattenNav(entries: ResolvedEntry[]): ResolvedItem[] {
	const out: ResolvedItem[] = [];
	for (const entry of entries) {
		if (entry.kind === "divider") continue;
		if (entry.kind === "group") {
			out.push(...entry.items);
			continue;
		}
		out.push(entry);
	}
	return out;
}

/** Segment-aware prefix match. `/blog` does not match `/blog-roundup`. */
function matchesPath(href: string, path: string): boolean {
	return path === href || path.startsWith(`${href}/`);
}

/**
 * The current row, resolved by LONGEST match.
 *
 * Longest-match is what lets the config drop the old `exact` flag: on
 * `/admin/pros` both `/admin` and `/admin/pros` match, and the deeper one
 * wins, so exactly one row is ever current.
 */
export function resolveActiveHref(
	entries: ResolvedEntry[],
	path: string,
): string | null {
	let best: string | null = null;
	for (const item of flattenNav(entries)) {
		if (item.kind === "action") continue;
		// An external link leaves the app; it is never "the page you are on".
		if (item.kind === "external") continue;
		if (!matchesPath(item.href, path)) continue;
		if (best === null || item.href.length > best.length) best = item.href;
	}
	return best;
}

/** The group that owns the current page, if any. Used to derive the fold. */
export function groupForPath(
	entries: ResolvedEntry[],
	path: string,
): string | undefined {
	const active = resolveActiveHref(entries, path);
	if (active === null) return undefined;
	for (const entry of entries) {
		if (entry.kind !== "group") continue;
		const owns = entry.items.some(
			(item) => item.kind === "link" && item.href === active,
		);
		if (owns) return entry.name;
	}
	return undefined;
}
